// src/scenes/RoundScene.ts
//
// Phase 5 composed the static round UI; Phase 6 wires the full spin loop:
//   1. Tap a fiche in the tray → it highlights gold (selected).
//   2. Tap a bet cell while a fiche is selected → placement created, cell
//      outlined in gold, fiche un-highlighted.
//   3. Tapping a placed cell again recalls the placement (frees the fiche).
//   4. SPIN button is gray until 3 placements made, then turns gold.
//   5. Press SPIN: pickSlotForSpin → wheel.spinTo → scoreSpin → popups → HUD.
//   6. After spin: discardHand, drawFiches(3), cycleBall, reset placements,
//      currentSpin++. Round/shop/ante progression is Phase 7.
//
// Phase 13 (BOARD-05, BOARD-06, UI-11, UI-12):
//   - Wheel moved to (88, 148), size 160px (wheel-left layout per 13-UI-SPEC).
//   - Table moved to (172, 68), reads gs.getState().boardLayout (BOARD-06).
//   - SPIN button repositioned to wheel hub: 52px circle at (88, 148), depth 20.
//   - pickSlotForSpin receives wheel: gs.getState().wheel (WHEEL-08).
//   - safety_net runOnZero gated on slot.kind === 'number' && slot.value === 0 (BETS-09).
//   - spinTo uses slot.position directly (aligns with composed wheel array index).
//   - Streak tracking narrows on slot.kind before reading slot.color.
//   - Wheel.setSlots called with composed run wheel (void wedges visible in Phase 15).

import Phaser from 'phaser';
import { Hud } from '../ui/Hud';
import { Wheel } from '../ui/Wheel';
import { Table } from '../ui/Table';
import { FicheTray } from '../ui/FicheTray';
import { ConsumableTray } from '../ui/ConsumableTray';
import { RelicBar } from '../ui/RelicBar';
import { ViewDeckModal } from '../ui/ViewDeckModal';
import { CellPickerModal } from '../ui/CellPickerModal';
import { ScorePopManager } from '../ui/ScorePopManager';
import { addCrispText } from '../ui/textHelper';
import { GameState } from '../state/GameState';
import type { Rarity, SlotNumber } from '../state/types';
import { ANTES } from '../content/antes';
import { pickSlotForSpin } from '../systems/spin';
import { scoreSpin } from '../systems/scoring';
import { saveRun, clearSave } from '../systems/save';
import { logEvent, resetLog } from '../systems/runLog';
import { runSpinStart, runOnZero, getRequiredFiches } from '../systems/relics';
import {
  pickBossModifier,
  pickDevilBoss,
  describeBossModifier,
  isBetBlockedByBoss,
  getEffectiveMods,
  computeFloodedIndices,
  pickBlockedCellIndices,
} from '../systems/boss';
import { isSameBet } from '../systems/bets';
import { reachable } from '../systems/chess';
import { expandPlacement } from '../systems/placements';
import { FICHES_BY_ID } from '../content/fiches';
import {
  useConsumable,
  CONSUMABLES_BY_ID,
  CONSUMABLES,
  consumableFromDef,
} from '../systems/consumables';
import { consumableSpritesheet } from '../content/consumables';
import { orthogonalNeighbors, subcellConnectedComponent } from '../systems/board';
import { pickFromArray } from '../systems/rng';
import type {
  BetType,
  BoardCell,
  BossModifier,
  ConsumableDef,
  ConsumableEffect,
  Fiche,
  Placement,
  Slot,
  SpinResult,
  SpinResult16,
} from '../state/types';

const ROUND_LABELS = ['Small', 'Big', 'Boss'] as const;

/**
 * Rotate a tetris shape 90° clockwise around its pivot.
 * Shapes use pivot-relative coordinates: the pivot cell is always at [0,0]
 * and remains fixed after rotation. Formula: [r,c] → [c,-r].
 */
function rotateCW90(
  shape: readonly (readonly [number, number])[],
): readonly (readonly [number, number])[] {
  return shape.map(([r, c]) => [c, -r] as const);
}

export class RoundScene extends Phaser.Scene {
  private gs!: GameState;
  private hud!: Hud;
  private tray!: FicheTray;
  private consumableTray!: ConsumableTray;
  private relicBar!: RelicBar;
  private wheel!: Wheel;
  private table!: Table;

  private viewDeckModal!: ViewDeckModal;
  private scorePopManager!: ScorePopManager;
  private placements: Placement[] = [];
  private placementRectSets: Phaser.GameObjects.Rectangle[][] = [];
  // Phase 11 polish: chip-token displayed on the bet cell so it's clear which
  // fiche went where. One Container per placement, parallel to placementRectSets.
  private placementChips: Phaser.GameObjects.Container[] = [];
  private currentSpin = 0;
  private roundScore = 0;
  private requiredFiches = 3;
  /** Fiches the player has explicitly right-clicked to skip for this spin. */
  private passedFicheIds = new Set<string>();
  // Phase 13 (UI-11): spin button is now an Arc (52px circle at wheel hub), not a Rectangle.
  private spinButton!: Phaser.GameObjects.Arc;
  private spinButtonLabel!: Phaser.GameObjects.Text;
  private isSpinning = false;
  // Phase 14 (UI-SPEC §Reachable-Preview Layer): chess reachable-set overlay at depth 9.
  private previewGraphics!: Phaser.GameObjects.Graphics;
  /** Persistent orange overlay showing cells blocked by placed tetris fiches. Depth 8 (below previewGraphics). */
  private blockedGraphics!: Phaser.GameObjects.Graphics;
  // Boss cellBlocker overlay + indices.
  private bossOverlayGraphics!: Phaser.GameObjects.Graphics;
  private bossBlockedCellIndices: number[] = [];
  // Per-round streak / zero tracking for relic hooks (Phase 8).
  // Streaks reset on opposite color OR zero. Zeros tracked so safety_net only fires once per round.
  private streakRed = 0;
  private streakBlack = 0;
  private zerosThisRound = 0;
  // Phase 9: boss-round modifier — picked at boss-round entry only.
  private bossModifier: BossModifier | null = null;
  // Phase 20: guard against double-tap opening multiple cell pickers.
  private _activePicker: CellPickerModal | null = null;
  // Phase 22: drag lifecycle state. Non-null while a fiche is being dragged.
  private _activeDrag: {
    ficheId: string;
    idx: number;
    ghost: Phaser.GameObjects.Arc;
    originX: number;
    originY: number;
    ghostOriginalColor: number;
    /** Set when drag originates from a placed chip token (move, not new placement). */
    fromBoardBet?: BetType;
    /** Current rotated shape override — only set for tetrisShape fiches. */
    currentShape?: readonly (readonly [number, number])[];
  } | null = null;
  private _dragMoveListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private _dragUpListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;

  constructor() {
    super({ key: 'Round' });
  }

  init(data: { gameState?: GameState }): void {
    this.gs = data?.gameState ?? new GameState();
    const s = this.gs.getState();
    // Reset log at ante 1 round 0 (fresh run); append for subsequent rounds.
    if (s.ante === 1 && s.roundIndex === 0) resetLog();
    logEvent('round_start', {
      ante: s.ante,
      round: s.roundIndex,
      money: s.money,
      board: s.boardLayout.cells.map((c) => ({ row: c.row, col: c.col, bet: c.bet })),
    });
    // requiredFiches reflects Croupier's Smile relic (4 fiches per spin if held).
    this.requiredFiches = getRequiredFiches(s.relics);
    if (this.gs.getState().ficheHand.length === 0) {
      this.gs.drawFiches(this.requiredFiches);
    }
    // Reset per-scene-mount state in case the scene is restarted
    this.placements = [];
    this.placementRectSets = [];
    this.placementChips = [];
    this.passedFicheIds = new Set();
    this.currentSpin = 0;
    this.roundScore = 0;
    this.isSpinning = false;
    // Per-round relic-tracking state (streaks + zero count) resets each round mount.
    this.streakRed = 0;
    this.streakBlack = 0;
    this.zerosThisRound = 0;
    // Phase 9: pick boss modifier on boss-round entry only (roundIndex === 2).
    // Non-boss rounds explicitly clear the slot so reset between rounds is clean.
    if (this.gs.getState().roundIndex === 2) {
      const s = this.gs.getState();
      if (s.ante === 3) {
        this.bossModifier = pickDevilBoss(this.gs.getRng());
      } else {
        this.bossModifier = pickBossModifier(this.gs.getRng());
      }
      // Generate cellBlocker indices if any effective mod is cellBlocker
      const effectiveMods = getEffectiveMods(this.bossModifier);
      if (effectiveMods.some((m) => m.type === 'cellBlocker')) {
        this.bossBlockedCellIndices = pickBlockedCellIndices(
          this.gs.getRng(),
          this.gs.getState().boardLayout.cells.length,
        );
      } else {
        this.bossBlockedCellIndices = [];
      }
    } else {
      this.bossModifier = null;
      this.bossBlockedCellIndices = [];
    }
  }

  create(): void {
    // Phase 17: navy background + bg-tile overlay (behind all game objects)
    this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x0d1b2a).setOrigin(0, 0);
    if (this.textures.exists('bg-tile')) {
      const tileW = 128;
      const tileH = 128;
      const cols = Math.ceil(this.scale.width / tileW) + 1;
      const rows = Math.ceil(this.scale.height / tileH) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          this.add
            .image(c * tileW, r * tileH, 'bg-tile')
            .setOrigin(0, 0)
            .setAlpha(0.08);
        }
      }
    }

    // Phase 16 (ROUND-07, FICHE-13): clear last-spin snapshot at each round start.
    this.gs.clearLastSpin();

    this.hud = new Hud(this, 0, 0);
    this.relicBar = new RelicBar(this, 0, 40);

    // Phase 13 (UI-18, WHEEL-11): view-deck modal — opens from HUD pill button.
    // Constructed before refresh() so the modal is ready before any user tap.
    this.viewDeckModal = new ViewDeckModal(this);
    this.hud.setOnViewDeckTap(() => {
      const s = this.gs.getState();
      this.viewDeckModal.open(s.numbersDeck, s.voidPositions);
    });

    // Layout: HUD(40) → RelicBar(70,ends y=110) → ConsumableTray(center y=130) → gap →
    // Wheel centered at (187,300), radius=130, bottom y=430 → Board at y=450 → Fiches y=600.
    this.wheel = new Wheel(this, 187, 300, 260);

    // Landscape board: full screen width, 130px tall (5 rows × 26px), below wheel.
    this.table = new Table(this, 0, 450, this.gs.getState().boardLayout, {
      boardWidth: 375,
      boardHeight: 130,
    });

    // Phase 14 (UI-SPEC §Reachable-Preview Layer): Graphics overlay at depth 9.
    // Positioned at (0,0) world space; drawRect calls use world coordinates from getCellBounds.
    // Depth 9: above board cells (depth 0 inside Table Container) but below chip tokens (depth 10–11)
    // and the spin button (depth 20). Tap-through preserved — Graphics has no interactive area.
    this.previewGraphics = this.add.graphics().setDepth(9);
    this.blockedGraphics = this.add.graphics().setDepth(8);
    this.bossOverlayGraphics = this.add.graphics().setDepth(8);

    // Board bottom: y=450 + 130 = 580. Gap 20px → tray at y=600.
    this.tray = new FicheTray(this, 0, 600);
    this.scorePopManager = new ScorePopManager(this);

    // Consumable tray: center y=130, between relicbar bottom (110) and wheel top (170).
    this.consumableTray = new ConsumableTray(this, 187, 130);
    this.consumableTray.onUse = (id) => {
      this.onConsumableTapped(id);
    };
    this.consumableTray.onDiscard = (id) => {
      this.gs.removeConsumable(id);
      this.refreshConsumableTray();
      this.refreshWheelScoreSlices();
      this.refreshBlockedCellsOverlay();
    };
    this.refreshConsumableTray();

    // Phase 13 (UI-12): pass the run's composed wheel to the Wheel UI so
    // void wedges render correctly. setSlots() called before refresh() so
    // wedge graphics include the run-specific composition before the first frame.
    this.wheel.setSlots(this.gs.getState().wheel);

    this.refresh();

    // SPIN button at wheel hub center (187, 300), depth 20 above wheel graphics.
    this.spinButton = this.add
      .circle(187, 300, 26, 0x333333)
      .setInteractive({ useHandCursor: true })
      .setDepth(20) as Phaser.GameObjects.Arc;
    this.spinButtonLabel = addCrispText(this, 187, 300, 'SPIN', {
      color: '#888888',
      fontSize: '14px',
    })
      .setOrigin(0.5)
      .setDepth(21);
    this.spinButton.on('pointerdown', () => {
      void this.onSpinTapped();
    });

    this.refreshSpinButton();
    this.refreshBossOverlay();

    // Suppress browser context menu so right-click can be used for pass/skip.
    this.input.mouse?.disableContextMenu();

    // R key: rotate active tetris fiche 90° clockwise during drag.
    this.input.keyboard?.on('keydown-R', () => {
      if (!this._activeDrag?.currentShape) return;
      this._activeDrag.currentShape = rotateCW90(this._activeDrag.currentShape);
      this._onDragMove(this.input.activePointer);
    });

    // Space: trigger spin (same as tapping the SPIN button).
    this.input.keyboard?.on('keydown-SPACE', () => {
      void this.onSpinTapped();
    });

    // Phase 9: boss-round banner + persistent HUD label. Unchanged.
    if (this.bossModifier) {
      this.showBossBanner(this.bossModifier);
      addCrispText(this, 187, 38, `BOSS: ${describeBossModifier(this.bossModifier).title}`, {
        color: '#ff4444',
        fontSize: '11px',
      }).setOrigin(0.5);
    }
  }

  // Phase 9: full-screen boss-modifier announcement; auto-dismisses at 2.5s.
  private showBossBanner(mod: BossModifier): void {
    const BOSS_IMAGE: Record<string, { key: string; frame: number }> = {
      noEvenMoney: { key: 'bosses-1', frame: 0 },
      noStraights: { key: 'bosses-1', frame: 1 },
      redHalf: { key: 'bosses-2', frame: 0 },
      blackHalf: { key: 'bosses-2', frame: 1 },
      zeroFlood: { key: 'bosses-2', frame: 2 },
      cellBlocker: { key: 'bosses-3', frame: 0 },
      nudge: { key: 'bosses-3', frame: 1 },
      devil: { key: 'bosses-3', frame: 2 },
    };
    const desc = describeBossModifier(mod);
    const imgEntry = BOSS_IMAGE[mod.type];

    const c = this.add.container(0, 0).setDepth(100);

    // Full-screen dark backdrop — tap to dismiss early
    const bg = this.add.rectangle(0, 0, 375, 667, 0x000000, 0.92).setOrigin(0, 0).setInteractive();
    c.add(bg);

    // Boss name
    c.add(
      addCrispText(this, 187, 60, desc.title, {
        color: '#d4af37',
        fontSize: '22px',
      }).setOrigin(0.5),
    );
    c.add(
      addCrispText(this, 187, 88, 'BOSS MODIFIER', {
        color: '#ff4444',
        fontSize: '11px',
      }).setOrigin(0.5),
    );

    // Boss image (full width)
    if (imgEntry && this.textures.exists(imgEntry.key)) {
      c.add(
        this.add
          .image(187, 230, imgEntry.key, imgEntry.frame)
          .setDisplaySize(375, 250)
          .setOrigin(0.5),
      );
    }

    // Description
    c.add(
      addCrispText(this, 187, 370, desc.subtitle, {
        color: '#cccccc',
        fontSize: '13px',
        align: 'center',
        wordWrap: { width: 320 },
      }).setOrigin(0.5, 0),
    );

    // Tap to dismiss
    c.add(
      addCrispText(this, 187, 630, 'Tap to continue', {
        color: '#666666',
        fontSize: '11px',
      }).setOrigin(0.5),
    );

    const dismiss = () => c.destroy();
    bg.on('pointerdown', dismiss);
    this.time.delayedCall(2500, dismiss);
  }

  // Phase 16 (RELIC-08): peek overlay — shows first fiche of next draw.
  // Auto-dismisses after 2000ms or on tap. Depth 150 renders above board/tray/relicBar.
  // Called from onSpinTapped() immediately before discardHand()+drawFiches() so that
  // no RNG is consumed between peekDrawFiches() and drawFiches() — the two calls see
  // identical deck+rng state and produce identical fiches ("peek shows X → round delivers X").
  // T-16-19: only the bg rectangle is setInteractive (card-sized); input outside
  //   card passes through to game elements at lower depth.
  private showPeekOverlay(fiche: Fiche): void {
    const CARD_W = 180;
    const CARD_H = 80;
    const CARD_X = 187; // horizontal center of 375px canvas
    const CARD_Y = 48; // top edge y

    // Container anchored at top-left of card so child coordinates are card-relative
    const container = this.add.container(CARD_X - CARD_W / 2, CARD_Y).setDepth(150);

    const bg = this.add
      .rectangle(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H, 0x062815)
      .setStrokeStyle(1, 0xd4af37);
    container.add(bg);

    const label = addCrispText(this, 90, 8, 'NEXT FICHE', {
      color: '#888888',
      fontSize: '11px',
    }).setOrigin(0.5, 0);
    const name = addCrispText(this, 90, 22, fiche.name, {
      color: '#ffffff',
      fontSize: '14px',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    const chipsMult = addCrispText(this, 90, 38, `+${fiche.chips} chips · ×${fiche.mult} mult`, {
      color: '#aaaaaa',
      fontSize: '11px',
    }).setOrigin(0.5, 0);
    const dismiss = addCrispText(this, 90, 56, 'Tap to dismiss', {
      color: '#555555',
      fontSize: '11px',
    }).setOrigin(0.5, 0);
    container.add([label, name, chipsMult, dismiss]);

    // Tap to dismiss immediately
    bg.setInteractive({ useHandCursor: false });
    const destroyOverlay = (): void => {
      if (container.active) container.destroy(true);
    };
    bg.on('pointerdown', destroyOverlay);

    // Auto-dismiss after 2000ms
    this.time.delayedCall(2000, destroyOverlay);
  }

  /**
   * Phase 23 (FICHE-23, pawn promotion): modal offering a chess piece upgrade
   * to a pawn that has marched 4 rows up in the same column. Tapping a
   * piece mutates the fiche's effect for the rest of the run; auto-dismiss
   * after 10s leaves the pawn untouched.
   *
   * Patterned on showPeekOverlay (depth 150, simple Container).
   */
  private showPawnPromotionModal(ficheId: string, onDone?: () => void): void {
    const PANEL_W = 240;
    const PANEL_H = 150;
    const CX = 187;
    const CY = 300;

    const container = this.add.container(CX - PANEL_W / 2, CY - PANEL_H / 2).setDepth(150);

    const bg = this.add
      .rectangle(PANEL_W / 2, PANEL_H / 2, PANEL_W, PANEL_H, 0x062815)
      .setStrokeStyle(1, 0xd4af37);
    container.add(bg);

    const title = addCrispText(this, PANEL_W / 2, 10, 'PAWN PROMOTION', {
      color: '#d4af37',
      fontSize: '12px',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    const sub = addCrispText(this, PANEL_W / 2, 28, 'Choose a new piece', {
      color: '#aaaaaa',
      fontSize: '11px',
    }).setOrigin(0.5, 0);
    container.add([title, sub]);

    let resolved = false;
    const destroyOverlay = (): void => {
      if (container.active) container.destroy(true);
    };

    const pieces: { piece: import('../systems/chess').ChessPiece; label: string }[] = [
      { piece: 'knight', label: 'Knight' },
      { piece: 'bishop', label: 'Bishop' },
      { piece: 'rook', label: 'Rook' },
      { piece: 'queen', label: 'Queen' },
    ];
    const BTN_W = 100;
    const BTN_H = 24;
    const COL_GAP = 8;
    const ROW_GAP = 8;
    const startX = PANEL_W / 2 - BTN_W - COL_GAP / 2;
    const startY = 56;

    pieces.forEach((p, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const bx = startX + col * (BTN_W + COL_GAP) + BTN_W / 2;
      const by = startY + row * (BTN_H + ROW_GAP) + BTN_H / 2;
      const btn = this.add
        .rectangle(bx, by, BTN_W, BTN_H, 0x1a4a1a)
        .setStrokeStyle(1, 0xd4af37)
        .setInteractive({ useHandCursor: true });
      const lbl = addCrispText(this, bx, by, p.label, {
        color: '#ffffff',
        fontSize: '12px',
      }).setOrigin(0.5, 0.5);
      container.add([btn, lbl]);
      btn.on('pointerdown', () => {
        if (resolved) return;
        resolved = true;
        this.gs.mutateFicheEffect(ficheId, { type: 'chessMove', piece: p.piece });
        destroyOverlay();
        this.refresh();
        onDone?.();
      });
    });

    // Auto-dismiss after 10s — pawn stays a pawn (no mutation).
    this.time.delayedCall(10000, () => {
      if (!resolved) {
        resolved = true;
        destroyOverlay();
        onDone?.();
      }
    });
  }

  private _chainPromotionsThen(ficheIds: string[], onAllDone: () => void): void {
    if (ficheIds.length === 0) {
      onAllDone();
      return;
    }
    const [first, ...rest] = ficheIds;
    this.showPawnPromotionModal(first!, () => this._chainPromotionsThen(rest, onAllDone));
  }

  /**
   * Phase 23 (FICHE-23, tetris row mult): brief gold overlay confirming a
   * row was completed by tetris fiches, granting +1 permanent flatMultBonus.
   * 2s auto-dismiss, simple centered text — patterned after showPeekOverlay
   * but without interactivity.
   */
  private showRowCompleteToast(row: number): void {
    const container = this.add.container(187, 440).setDepth(160);
    const bg = this.add.rectangle(0, 0, 280, 60, 0x062815, 0.95).setStrokeStyle(1, 0xd4af37);
    const title = addCrispText(this, 0, -10, `ROW ${row} COMPLETED!`, {
      color: '#d4af37',
      fontSize: '14px',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    const sub = addCrispText(this, 0, 10, '+1 mult permanent', {
      color: '#ffffff',
      fontSize: '11px',
    }).setOrigin(0.5, 0.5);
    container.add([bg, title, sub]);
    this.time.delayedCall(2000, () => {
      if (container.active) container.destroy(true);
    });
  }

  /**
   * Post-spin score breakdown panel. Shows each fiche's contribution for 3s.
   * Appears below the result text, above the fiche tray. Tap to dismiss early.
   */
  private showScoreBreakdown(result: SpinResult): void {
    const s = this.gs.getState();
    // halfPayouts boss removed; the breakdown's "÷2 (boss)" footer is now disabled.
    const isHalfPayouts = false;
    // Hit rows use 2 lines (name + formula); miss rows use 1 line.
    const HIT_H = 24;
    const MISS_H = 18;
    const FOOTER_H = 18;
    const PAD = 8;
    const W = 340;

    const rowHeights = result.perFicheScores.map((ps) => (ps.hit ? HIT_H : MISS_H));
    const totalRowPx = rowHeights.reduce((a, b) => a + b, 0);
    const footerRows = isHalfPayouts ? 2 : 1;
    const H = PAD * 2 + totalRowPx + footerRows * FOOTER_H + 4;
    const panelY = 440;
    const container = this.add.container(187 - W / 2, panelY).setDepth(160);

    const bg = this.add.rectangle(W / 2, H / 2, W, H, 0x0a1a0f, 0.92).setStrokeStyle(1, 0x2a4a2a);
    container.add(bg);

    const allFiches = [...s.ficheHand, ...s.ficheDiscard, ...s.ficheDeck];
    let rowY = PAD;
    result.perFicheScores.forEach((ps) => {
      const fiche = allFiches.find((f) => f.id === ps.ficheId);
      const name = fiche?.name ?? ps.ficheId;

      if (ps.hit) {
        // Line 1: ✓ name   ×mult   +total
        container.add([
          addCrispText(this, PAD, rowY, '✓', { color: '#44cc66', fontSize: '11px' }),
          addCrispText(this, PAD + 14, rowY, name, { color: '#ffffff', fontSize: '11px' }),
        ]);
        if (ps.mult > 1) {
          container.add(
            addCrispText(this, W / 2, rowY, `×${ps.mult}`, {
              color: '#f0c040',
              fontSize: '11px',
            }).setOrigin(0.5, 0),
          );
        }
        container.add(
          addCrispText(this, W - PAD, rowY, `+${ps.total}`, {
            color: '#d4af37',
            fontSize: '11px',
          }).setOrigin(1, 0),
        );
        // Line 2: chips chips × mult × payout = total (grey formula)
        const formula = `${ps.chips} chips × ${ps.mult} × ${ps.payout} = ${ps.total}`;
        container.add(
          addCrispText(this, PAD + 14, rowY + 13, formula, {
            color: '#666666',
            fontSize: '9px',
          }),
        );
        rowY += HIT_H;
      } else {
        container.add([
          addCrispText(this, PAD, rowY, '✗', { color: '#884444', fontSize: '11px' }),
          addCrispText(this, PAD + 14, rowY, name, { color: '#555555', fontSize: '11px' }),
        ]);
        rowY += MISS_H;
      }
    });

    const subtotal = result.perFicheScores.reduce((acc, p) => acc + p.total, 0);

    if (isHalfPayouts) {
      container.add(
        addCrispText(this, W - PAD, rowY, `÷2 (boss)  ${subtotal} → ${result.spinScore}`, {
          color: '#ff9944',
          fontSize: '10px',
        }).setOrigin(1, 0),
      );
      rowY += FOOTER_H;
    }

    container.add(
      addCrispText(this, W - PAD, rowY, `Score: ${result.spinScore}`, {
        color: '#d4af37',
        fontSize: '11px',
        fontStyle: 'bold',
      }).setOrigin(1, 0),
    );

    bg.setInteractive({ useHandCursor: false });
    const destroy = (): void => {
      if (container.active) container.destroy(true);
    };
    bg.on('pointerdown', destroy);
    this.time.delayedCall(3000, destroy);
  }

  // -------- input handlers --------

  /** Place a fiche on a bet cell (drag model, Phase 22). Called from _onDragUp
   * on valid drop (explicitFicheId provided). The recall branch fires when a
   * placement for `bet` already exists — explicitFicheId is ignored in that path. */
  private onBetPlace(
    bet: BetType,
    rects: Phaser.GameObjects.Rectangle[],
    explicitFicheId?: string,
    insertIndex?: number,
  ): void {
    if (this.isSpinning) return;
    // Phase 9: boss-modifier bet gate. Visual-only feedback (camera shake);
    // no error message, no toast — just refuses the placement.
    if (this.bossModifier && isBetBlockedByBoss(this.bossModifier, bet.kind)) {
      this.cameras.main.shake(100, 0.005);
      return;
    }
    // If a placement already exists for this exact bet, recall it.
    const existingIdx = this.placements.findIndex((p) => isSameBet(p.bet, bet));
    if (existingIdx >= 0) {
      const recalledFicheId = this.placements[existingIdx]?.ficheId;
      const recalledFiche = recalledFicheId
        ? this.gs.getState().ficheHand.find((f) => f.id === recalledFicheId)
        : undefined;
      logEvent('fiche_recalled', {
        ficheId: recalledFicheId,
        name: recalledFiche?.name,
        defId: recalledFiche?.defId,
        bet,
      });
      this.placements.splice(existingIdx, 1);
      // Clear stroke on ALL rects of the recalled placement
      const recalled = this.placementRectSets.splice(existingIdx, 1)[0] ?? [];
      recalled.forEach((r) => r.setStrokeStyle(0.5, 0xffffff));
      const chip = this.placementChips.splice(existingIdx, 1)[0];
      if (chip) chip.destroy();
      // Phase 14: clear chess reachable-set preview on recall (UI-SPEC §Interaction).
      this.previewGraphics.clear();
      this.tray.markUsed(this.placements.map((p) => p.ficheId));
      this.refreshSpinButton();
      this.refreshConsumableTray();
      this.refreshWheelScoreSlices();
      this.refreshBlockedCellsOverlay();
      return;
    }
    const ficheId = explicitFicheId ?? null;
    if (!ficheId) return;
    if (this.placements.length >= this.requiredFiches) return;
    // Defense-in-depth: same fiche cannot appear in two placements.
    if (this.placements.some((p) => p.ficheId === ficheId)) return;
    // Use splice-at-index for chip-moves so the fiche stays at its original chain position.
    const insertAt =
      insertIndex !== undefined && insertIndex >= 0 && insertIndex <= this.placements.length
        ? insertIndex
        : this.placements.length;
    this.placements.splice(insertAt, 0, { ficheId, bet });
    const fiche = this.gs.getState().ficheHand.find((f) => f.id === ficheId);
    logEvent('fiche_placed', {
      ficheId,
      name: fiche?.name,
      defId: fiche?.defId,
      bet,
      placements: this.placements.length,
    });
    this.placementRectSets.splice(insertAt, 0, rects);
    // Gold stroke on ALL covered rects
    rects.forEach((r) => r.setStrokeStyle(2, 0xd4af37));
    // Phase 14: draw reachable-set preview for chess fiches (UI-SPEC §Interaction).
    // FI-02 fix: rebuild ALL chess previews in one clear+draw pass so placing a
    // second chess fiche does not erase the first fiche's reachable-set highlight.
    this.rebuildAllChessPreviews();
    // Chip token centered on average of all covered rects
    if (fiche) {
      const tokenX = rects.reduce((sum, r) => sum + r.getBounds().centerX, 0) / rects.length;
      const tokenY = rects.reduce((sum, r) => sum + r.getBounds().centerY, 0) / rects.length;
      const chip = this.makeChipToken(tokenX, tokenY, fiche.chips, fiche.rarity, { ficheId, bet });
      this.placementChips.splice(insertAt, 0, chip);
    } else {
      // Pad the array so indices stay aligned even if the fiche couldn't be
      // looked up (shouldn't happen in practice).
      this.placementChips.splice(insertAt, 0, this.add.container(0, 0));
    }
    this.tray.markUsed(this.placements.map((p) => p.ficheId));
    this.refreshSpinButton();
    this.refreshConsumableTray();
    this.refreshWheelScoreSlices();
    this.refreshBlockedCellsOverlay();
  }

  // -------- Phase 22: drag-to-place lifecycle --------

  /** Returns the fiche for drag hit-testing, substituting the current rotated shape if active. */
  private _getDragFiche(ficheId: string): Fiche | undefined {
    const fiche = this.gs.getState().ficheHand.find((f) => f.id === ficheId);
    if (
      !fiche ||
      !this._activeDrag ||
      this._activeDrag.ficheId !== ficheId ||
      !this._activeDrag.currentShape
    )
      return fiche;
    return {
      ...fiche,
      effect: { type: 'tetrisShape', shape: this._activeDrag.currentShape },
    } as Fiche;
  }

  private onFicheDragStart(ficheId: string, idx: number, ghost: Phaser.GameObjects.Arc): void {
    if (this.isSpinning) {
      ghost.destroy();
      return;
    }
    const fiche = this.gs.getState().ficheHand.find((f) => f.id === ficheId);
    logEvent('fiche_drag_start', { ficheId, defId: fiche?.defId });
    // Compute snap-back origin from tray geometry
    const originX = this.tray.slotCenterX(idx, this.requiredFiches);
    const originY = this.tray.slotCenterY();

    const tetrisShape =
      (fiche?.effect?.type as string) === 'tetrisShape'
        ? (fiche!.effect as { shape: readonly (readonly [number, number])[] }).shape
        : undefined;

    this._activeDrag = {
      ficheId,
      idx,
      ghost,
      originX,
      originY,
      ghostOriginalColor: ghost.fillColor,
      ...(tetrisShape ? { currentShape: tetrisShape } : {}),
    };

    // Register scene-level listeners for the duration of this drag
    this._dragMoveListener = (ptr: Phaser.Input.Pointer) => {
      this._onDragMove(ptr);
    };
    this._dragUpListener = (ptr: Phaser.Input.Pointer) => {
      this._onDragUp(ptr);
    };
    this.input.on('pointermove', this._dragMoveListener);
    this.input.on('pointerup', this._dragUpListener);
  }

  private _hitTestCell(x: number, y: number): BoardCell | null {
    const s = this.gs.getState();
    for (const cell of s.boardLayout.cells) {
      const bounds = this.table.getCellBounds(cell.bet);
      if (bounds && bounds.contains(x, y)) return cell;
    }
    return null;
  }

  private _isDragValid(ficheId: string, cell: BoardCell, fromBoardBet?: BetType): boolean {
    // NOTE: mirrors onBetPlace validity checks — keep in sync if boss modifiers change
    const s = this.gs.getState();
    const bet = cell.bet;
    // Boss restriction gate
    if (this.bossModifier && isBetBlockedByBoss(this.bossModifier, bet.kind)) return false;
    // cellBlocker: flat cell index check
    if (this.bossBlockedCellIndices.length > 0) {
      const cellIdx = s.boardLayout.cells.findIndex(
        (c) => c.row === cell.row && c.col === cell.col,
      );
      if (cellIdx >= 0 && this.bossBlockedCellIndices.includes(cellIdx)) return false;
    }
    // For chip-move drags the fiche occupies one slot already — moving doesn't add to count.
    const effectiveCount = fromBoardBet ? this.placements.length - 1 : this.placements.length;
    if (effectiveCount >= this.requiredFiches) return false;
    // Cannot double-place same fiche — but skip when moving (fiche IS already placed).
    if (!fromBoardBet && this.placements.some((p) => p.ficheId === ficheId)) return false;
    // Get covered cells for overlap check (uses rotated shape if tetris drag active)
    const fiche = this._getDragFiche(ficheId);
    if (!fiche) return false;
    const testPlacement = { ficheId, bet };
    const covered = expandPlacement(testPlacement, s.boardLayout, fiche, this.placements);
    // Chess pieces: no additional off-grid rejection.
    // `reachable` already filters squares outside grid dims via inBounds/walkRay bounds.
    // Null gaps within the grid (e.g. row 0/4 at number columns) are silently dropped
    // by expandPlacement — rejecting on null gaps incorrectly blocks edge placements.
    // Phase 23 (FICHE-23): tetris off-grid detection. expandPlacement() drops
    // off-grid cells silently, so if the resulting `covered` length is shorter
    // than the shape, the player is trying to drop the shape over the board
    // edge — reject the placement.
    if ((fiche.effect?.type as string) === 'tetrisShape') {
      const shape = (fiche.effect as { shape: readonly unknown[] }).shape;
      if (covered.length < shape.length) return false;
    }
    // Subcell connected-component restriction: placing on a subcell blocks all other
    // subcells of the same parent reachable via 4-connected traversal through same-parent
    // cells. Subcells separated by a non-same-parent cell can be independently occupied.
    if (bet.kind === 'dozen-sub' || bet.kind === 'outside-sub') {
      const component = subcellConnectedComponent(s.boardLayout, cell);
      const componentKeys = new Set(component.map((c) => JSON.stringify(c.bet)));
      // Exclude the old placement (fromBoardBet) from the block check — that slot is being vacated.
      if (
        this.placements.some(
          (p) =>
            componentKeys.has(JSON.stringify(p.bet)) &&
            !(fromBoardBet && isSameBet(p.bet, fromBoardBet)),
        )
      )
        return false;
    }
    // Overlap check: only applies when at least one party is a Tetris fiche.
    // Regular and chess fiches may freely share covered cells.
    // Cast to string for forward-compat: 'tetrisShape' lands in Phase 23.
    const isIncomingTetris = (fiche.effect?.type as string) === 'tetrisShape';
    for (const existing of this.placements) {
      const existingFiche = s.ficheHand.find((f) => f.id === existing.ficheId);
      if (!existingFiche) continue;
      const isExistingTetris = (existingFiche.effect?.type as string) === 'tetrisShape';
      if (!isIncomingTetris && !isExistingTetris) continue; // neither is Tetris → allow
      const existingCovered = expandPlacement(
        existing,
        s.boardLayout,
        existingFiche,
        this.placements,
      );
      const existingBetKeys = new Set(existingCovered.map((c) => JSON.stringify(c)));
      if (covered.some((c) => existingBetKeys.has(JSON.stringify(c)))) return false;
    }
    return true;
  }

  private _onDragMove(ptr: Phaser.Input.Pointer): void {
    if (!this._activeDrag) return;
    const { ghost, ficheId, fromBoardBet } = this._activeDrag;

    // Move ghost to pointer
    ghost.setPosition(ptr.worldX, ptr.worldY);

    // Hit-test board cell under pointer
    const cell = this._hitTestCell(ptr.worldX, ptr.worldY);

    this.previewGraphics.clear();

    if (!cell) {
      // Off-grid: red ghost, no preview
      ghost.setFillStyle(0xff4444).setAlpha(0.4);
      return;
    }

    const valid = this._isDragValid(ficheId, cell, fromBoardBet);

    if (valid) {
      // Restore ghost to original rarity color (undoes any red applied on previous frame)
      ghost.setFillStyle(this._activeDrag!.ghostOriginalColor).setAlpha(0.75);
      // Gold preview: show covered cells (uses rotated shape if tetris drag active)
      const s = this.gs.getState();
      const fiche = this._getDragFiche(ficheId);
      if (fiche) {
        const testPlacement = { ficheId, bet: cell.bet };
        const covered = expandPlacement(testPlacement, s.boardLayout, fiche, this.placements);
        for (const c of covered) {
          const bounds = this.table.getCellBounds(c);
          if (!bounds) continue;
          this.previewGraphics.fillStyle(0xd4af37, 0.18);
          this.previewGraphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
          this.previewGraphics.lineStyle(1.5, 0xd4af37, 0.7);
          this.previewGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
        }
      }
    } else {
      // Invalid: red ghost + red preview
      ghost.setFillStyle(0xff4444).setAlpha(0.4);
      const s = this.gs.getState();
      const fiche = this._getDragFiche(ficheId);
      if (fiche) {
        const testPlacement = { ficheId, bet: cell.bet };
        const covered = expandPlacement(testPlacement, s.boardLayout, fiche, this.placements);
        for (const c of covered) {
          const bounds = this.table.getCellBounds(c);
          if (!bounds) continue;
          this.previewGraphics.fillStyle(0xff4444, 0.18);
          this.previewGraphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
        }
      }
    }
  }

  private _onDragUp(ptr: Phaser.Input.Pointer): void {
    if (!this._activeDrag) return;
    const { ficheId, ghost, originX, originY, fromBoardBet, currentShape } = this._activeDrag;

    // Clean up scene-level listeners immediately
    if (this._dragMoveListener) {
      this.input.off('pointermove', this._dragMoveListener);
      this._dragMoveListener = null;
    }
    if (this._dragUpListener) {
      this.input.off('pointerup', this._dragUpListener);
      this._dragUpListener = null;
    }

    this.previewGraphics.clear();

    // Tray recall zone: dropping a placed chip over the fiche tray recalls it.
    const trayTopY = this.tray.y;
    const droppedOnTray = ptr.worldY >= trayTopY - 20 && fromBoardBet;

    if (droppedOnTray) {
      this._activeDrag = null;
      ghost.destroy();
      // Recall the placement — identical to tapping its board cell.
      const oldIdx = this.placements.findIndex((p) => isSameBet(p.bet, fromBoardBet));
      if (oldIdx >= 0) {
        this.placements.splice(oldIdx, 1);
        const oldRects = this.placementRectSets.splice(oldIdx, 1)[0] ?? [];
        oldRects.forEach((r) => r.setStrokeStyle(0.5, 0xffffff));
        const oldChip = this.placementChips.splice(oldIdx, 1)[0];
        if (oldChip) oldChip.destroy();
        this.previewGraphics.clear();
        this.tray.markUsed(this.placements.map((p) => p.ficheId));
        this.refreshSpinButton();
        this.refreshConsumableTray();
        this.refreshWheelScoreSlices();
        this.refreshBlockedCellsOverlay();
      }
      return;
    }

    const cell = this._hitTestCell(ptr.worldX, ptr.worldY);
    // Validate BEFORE clearing _activeDrag so _getDragFiche can read the rotated shape.
    const valid = cell ? this._isDragValid(ficheId, cell, fromBoardBet) : false;
    this._activeDrag = null;

    if (!valid && cell) {
      const rejectedFiche = this.gs.getState().ficheHand.find((f) => f.id === ficheId);
      logEvent('fiche_rejected', {
        ficheId,
        name: rejectedFiche?.name,
        defId: rejectedFiche?.defId,
        bet: cell.bet,
        reason:
          this.placements.length >= this.requiredFiches
            ? 'max_placements'
            : this.placements.some((p) => p.ficheId === ficheId)
              ? 'double_place'
              : 'other',
      });
    }

    if (valid && cell) {
      ghost.destroy();
      // Commit rotated shape into GameState before placement so scoring reads the right shape.
      if (currentShape) {
        this.gs.mutateFicheEffect(ficheId, { type: 'tetrisShape', shape: currentShape });
      }
      // Chip-move: recall old placement first, then place on new cell.
      // Preserve oldIdx so the re-inserted placement keeps its chain-bonus position.
      let chipMoveOldIdx: number | undefined;
      if (fromBoardBet) {
        const oldIdx = this.placements.findIndex((p) => isSameBet(p.bet, fromBoardBet));
        if (oldIdx >= 0) {
          chipMoveOldIdx = oldIdx;
          this.placements.splice(oldIdx, 1);
          const oldRects = this.placementRectSets.splice(oldIdx, 1)[0] ?? [];
          oldRects.forEach((r) => r.setStrokeStyle(0.5, 0xffffff));
          const oldChip = this.placementChips.splice(oldIdx, 1)[0];
          if (oldChip) oldChip.destroy();
        }
      }
      const rects = this.table.getRectsForBet(cell.bet);
      this.onBetPlace(cell.bet, rects, ficheId, chipMoveOldIdx);
    } else {
      // Cancel: snap ghost back to origin
      this.tweens.add({
        targets: ghost,
        x: originX,
        y: originY,
        duration: 200,
        ease: 'Quad.easeOut',
        onComplete: () => {
          ghost.destroy();
          this.tray.markUsed(this.placements.map((p) => p.ficheId));
        },
      });
      this.cameras.main.shake(200, 0.005);
    }
  }

  /**
   * Build a small chip-style token at pre-computed world-space coordinates.
   * Container holds a circle (rarity-coded) + the chip count in black.
   * tokenX/tokenY are the average center of all covered rects for split/corner bets.
   */
  private makeChipToken(
    tokenX: number,
    tokenY: number,
    chips: number,
    rarity: Rarity,
    placement: Placement,
  ): Phaser.GameObjects.Container {
    const radius = 5; // fixed small radius — no single rect to derive size from
    // Phase 12: legendary added (gold). v1 fiches retain pre-existing rarities.
    const fill =
      rarity === 'legendary'
        ? 0xd4af37
        : rarity === 'rare'
          ? 0xa259d6
          : rarity === 'uncommon'
            ? 0x4a90e2
            : 0xffffff;
    const circle = this.add
      .circle(tokenX, tokenY, Math.max(6, radius), fill)
      .setStrokeStyle(1.5, 0x000000)
      .setDepth(10);
    // Chip drag-to-move: pressing the chip starts the standard ghost-drag system
    // so the fiche can be repositioned with full visual feedback.
    circle.setInteractive({ useHandCursor: true });
    circle.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      if (this.isSpinning || this._activeDrag) return;
      const ghostColor = fill;
      const ghost = this.add
        .circle(ptr.worldX, ptr.worldY, 36, ghostColor)
        .setStrokeStyle(2, 0x000000)
        .setAlpha(0.75)
        .setDepth(25);
      const ficheIdx = this.gs.getState().ficheHand.findIndex((f) => f.id === placement.ficheId);
      const chipFiche = this.gs.getState().ficheHand.find((f) => f.id === placement.ficheId);
      const chipTetrisShape =
        (chipFiche?.effect?.type as string) === 'tetrisShape'
          ? (chipFiche!.effect as { shape: readonly (readonly [number, number])[] }).shape
          : undefined;
      this._activeDrag = {
        ficheId: placement.ficheId,
        idx: ficheIdx >= 0 ? ficheIdx : 0,
        ghost,
        originX: tokenX,
        originY: tokenY,
        ghostOriginalColor: ghostColor,
        fromBoardBet: placement.bet,
        ...(chipTetrisShape ? { currentShape: chipTetrisShape } : {}),
      };
      this._dragMoveListener = (p: Phaser.Input.Pointer) => this._onDragMove(p);
      this._dragUpListener = (p: Phaser.Input.Pointer) => this._onDragUp(p);
      this.input.on('pointermove', this._dragMoveListener);
      this.input.on('pointerup', this._dragUpListener);
    });
    const label = addCrispText(this, tokenX, tokenY, String(chips), {
      color: '#000000',
      fontSize: '10px',
    })
      .setOrigin(0.5)
      .setDepth(11);
    return this.add.container(0, 0, [circle, label]).setDepth(10);
  }

  /**
   * Phase 14 (CHESS-04): Stable bet equality — mirrors betsMatchForPreview in Table.ts
   * (duplicated here to resolve board cell lookups without importing from the UI layer).
   *
   * FI-04 fix: split and corner comparisons sort numbers before comparing so
   * bets stored in any order match correctly, consistent with isSameBet in bets.ts
   * and the corner sorting in betKey in placements.ts.
   */
  private betsMatchForPreview(a: BetType, b: BetType): boolean {
    if (a.kind !== b.kind) return false;
    switch (a.kind) {
      case 'straight':
        return a.number === (b as typeof a).number;
      case 'dozen':
      case 'column':
        return a.which === (b as typeof a).which;
      case 'split': {
        const sa = [...a.numbers].sort((x, y) => x - y);
        const sb = [...(b as typeof a).numbers].sort((x, y) => x - y);
        return sa[0] === sb[0] && sa[1] === sb[1];
      }
      case 'corner':
        return (
          [...a.numbers].sort((x, y) => x - y).join(',') ===
          [...(b as typeof a).numbers].sort((x, y) => x - y).join(',')
        );
      // Phase 21 (BOARD-21): outside-sub match by parentKind + sorted numbers
      case 'outside-sub': {
        const bb = b as typeof a;
        if (a.parentKind !== bb.parentKind) return false;
        if (a.numbers.length !== bb.numbers.length) return false;
        const sa = [...a.numbers].sort((x, y) => x - y);
        const sb = [...bb.numbers].sort((x, y) => x - y);
        return sa.every((v, i) => v === sb[i]);
      }
      case 'dozen-sub': {
        const bb = b as typeof a;
        return a.parentWhich === bb.parentWhich && a.subIdx === bb.subIdx;
      }
      default:
        // red, black, even, odd, low, high — kind match is sufficient
        return true;
    }
  }

  /**
   * Phase 14 (UI-SPEC §Reachable-Preview Layer): Rebuild all chess reachable-set
   * highlights from scratch — clear once, then draw each chess placement in order.
   *
   * FI-02 fix: placing a second chess fiche no longer erases the first fiche's
   * preview because we clear the canvas only ONCE here (not inside _drawChessReach).
   *
   * Called from onBetPlace() whenever a chess fiche is placed. Also called on
   * deselect and recall paths through the existing previewGraphics.clear() calls
   * (those clear-only paths are unaffected by this refactor).
   */
  private rebuildAllChessPreviews(): void {
    this.previewGraphics.clear();
    const s = this.gs.getState();
    for (const p of this.placements) {
      const fiche = s.ficheHand.find((f) => f.id === p.ficheId);
      if (!fiche) continue;
      const def = FICHES_BY_ID[fiche.defId];
      if (def?.effect?.type !== 'chessMove') continue;
      this._drawChessReach(fiche.defId, p.bet);
    }
  }

  /**
   * Draw the reachable-set highlight for a single chess piece at `originBet`.
   * Does NOT call previewGraphics.clear() — caller is responsible for clearing
   * before the batch (see rebuildAllChessPreviews).
   */
  private _drawChessReach(defId: string, originBet: BetType): void {
    const s = this.gs.getState();
    const def = FICHES_BY_ID[defId];
    if (def?.effect?.type !== 'chessMove') return;

    const boardLayout = s.boardLayout;
    const origin = boardLayout.cells.find((c) => this.betsMatchForPreview(c.bet, originBet));
    if (!origin) return; // void/missing origin — no preview (CHESS-06)

    // Build blocker set from all other placed fiches' origin positions.
    const blockers = new Set<string>();
    for (const p of this.placements) {
      if (isSameBet(p.bet, originBet)) continue;
      const cell = boardLayout.cells.find((c) => this.betsMatchForPreview(c.bet, p.bet));
      if (cell) blockers.add(`${cell.row},${cell.col}`);
    }

    const reach = reachable(
      def.effect.piece,
      { row: origin.row, col: origin.col },
      boardLayout.dims,
      blockers,
    );

    for (const sq of reach) {
      const cell = boardLayout.grid[sq.row]?.[sq.col];
      if (!cell) continue;
      const bounds = this.table.getCellBounds(cell.bet);
      if (!bounds) continue;
      // Fill: #ffd700 alpha 0.18 (UI-SPEC §Color: reachable-set highlight)
      this.previewGraphics.fillStyle(0xd4af37, 0.18);
      this.previewGraphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
      // Stroke: #ffd700 alpha 0.70, 1.5px (UI-SPEC §Color)
      this.previewGraphics.lineStyle(1.5, 0xd4af37, 0.7);
      this.previewGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
  }

  // -------- Phase 15 (UI-17): consumable tray --------

  private refreshConsumableTray(): void {
    const state = this.gs.getState();
    const slots = state.consumables.map((c) => {
      const def = CONSUMABLES_BY_ID[c.defId];
      return {
        id: c.id,
        defId: c.defId,
        name: def?.name ?? c.defId,
        description: def?.description ?? '',
        rarity: c.rarity,
      };
    });
    this.consumableTray.draw(slots);
    // Disable if any fiche placed (pre-placement guard, CONSUMABLE-03) or spinning
    this.consumableTray.setDisabled(this.placements.length > 0 || this.isSpinning);
  }

  /**
   * Compute a hypothetical score for each wheel slot given current placements,
   * then tell the wheel to draw score-preview pie slices. Called after any
   * placement change. Clears slices when no placements are active.
   */
  private refreshWheelScoreSlices(): void {
    if (this.placements.length === 0) {
      this.wheel.clearScoreSlices();
      return;
    }
    const s = this.gs.getState();
    const fichesById: Record<string, import('../state/types').Fiche> = {};
    for (const f of s.ficheHand) fichesById[f.id] = f;

    // Pre-compute chess bet expansions (pure, no RNG)
    const bossBlockers = this._computeBossBlockerSet(s.boardLayout);
    const expandedBets = new Map<string, BetType[]>();
    for (const p of this.placements) {
      const fiche = fichesById[p.ficheId];
      if (fiche)
        expandedBets.set(
          p.ficheId,
          expandPlacement(p, s.boardLayout, fiche, this.placements, bossBlockers),
        );
    }

    // Approximate flatMultBonus using relic hooks (pure — no state mutation)
    const previewCtx = runSpinStart(s.relics, {
      ball: s.queuedBall ?? s.nextBall,
      isFirstSpinOfRound: this.currentSpin === 0,
      consecutiveRedHits: this.streakRed,
      consecutiveBlackHits: this.streakBlack,
      flatMultBonus: 0,
      payoutMultiplier: 1,
      spinMoneyDelta: 0,
      voidPositionsCount: s.voidPositions.length,
    });
    // Phase 23 (FICHE-23): apply persistent tetris-row mult bonuses to the
    // wheel-slice preview so the player sees the correct projected score.
    previewCtx.flatMultBonus += s.tetrisCompletedRows.length;

    const target = this.getRoundTarget();
    // Use remaining score so a slot that closes the round reaches full length.
    const remaining = Math.max(target - this.roundScore, 1);
    const previewEffectiveMods = getEffectiveMods(this.bossModifier);
    const previewBossRedHalf = previewEffectiveMods.some((m) => m.type === 'redHalf');
    const previewBossBlackHalf = previewEffectiveMods.some((m) => m.type === 'blackHalf');
    const scores = s.wheel.map(
      (slot) =>
        scoreSpin({
          placements: this.placements,
          fichesById,
          slot,
          ball: s.queuedBall ?? s.nextBall ?? null,
          payoutMultiplier: previewCtx.payoutMultiplier,
          bossHalfPayouts: false,
          bossRedHalf: previewBossRedHalf,
          bossBlackHalf: previewBossBlackHalf,
          flatMultBonus: previewCtx.flatMultBonus,
          expandedBets,
          boardLayout: s.boardLayout,
          standardFicheCount: [...s.ficheDeck, ...s.ficheDiscard, ...s.ficheHand].filter(
            (f) => f.defId === 'standard',
          ).length,
          lastSpin: s.lastSpin,
          oneForAll: s.relics.some((r) => r.defId === 'one_for_all'),
        }).spinScore,
    );

    this.wheel.setScoreSlices(scores, remaining);
  }

  /**
   * Compute the set of "row,col" keys for boss-blocked cells. Used as
   * extraBlockers for chess-line expansion so that chess fiches stop at
   * blocked cells, not pass through them.
   */
  private _computeBossBlockerSet(boardLayout: import('../state/types').BoardLayout): Set<string> {
    const out = new Set<string>();
    for (const idx of this.bossBlockedCellIndices) {
      const cell = boardLayout.cells[idx];
      if (cell) out.add(`${cell.row},${cell.col}`);
    }
    return out;
  }

  /** Redraw the persistent red overlay for cells blocked by the cellBlocker boss modifier. */
  private refreshBossOverlay(): void {
    this.bossOverlayGraphics.clear();
    if (this.bossBlockedCellIndices.length === 0) return;
    const s = this.gs.getState();
    for (const idx of this.bossBlockedCellIndices) {
      const cell = s.boardLayout.cells[idx];
      if (!cell) continue;
      const bounds = this.table.getCellBounds(cell.bet);
      if (!bounds) continue;
      this.bossOverlayGraphics.fillStyle(0xff2222, 0.3);
      this.bossOverlayGraphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
      this.bossOverlayGraphics.lineStyle(1, 0xff2222, 0.7);
      this.bossOverlayGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
  }

  /** Redraw the persistent orange overlay for cells occupied by placed tetris fiches. */
  private refreshBlockedCellsOverlay(): void {
    this.blockedGraphics.clear();
    const s = this.gs.getState();
    for (const p of this.placements) {
      const fiche = s.ficheHand.find((f) => f.id === p.ficheId);
      if (!fiche || (fiche.effect?.type as string) !== 'tetrisShape') continue;
      const covered = expandPlacement(p, s.boardLayout, fiche, this.placements);
      for (const b of covered) {
        const bounds = this.table.getCellBounds(b);
        if (!bounds) continue;
        this.blockedGraphics.fillStyle(0xff6600, 0.18);
        this.blockedGraphics.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
        this.blockedGraphics.lineStyle(1, 0xff6600, 0.45);
        this.blockedGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
      }
    }
  }

  private onConsumableTapped(id: string): void {
    if (this.isSpinning) return;
    const consumable = this.gs.getState().consumables.find((c) => c.id === id);
    if (!consumable) return;
    const def = CONSUMABLES_BY_ID[consumable.defId];
    if (!def) return;

    // Show confirm modal; on "Drink" proceed with the actual effect
    this._showConsumableConfirm(
      def.name,
      def.description,
      consumableSpritesheet(consumable.defId),
      () => {
        logEvent('consumable_use', { defId: consumable.defId, name: def.name });
        this._doUseConsumable(id);
      },
    );
  }

  private _showConsumableConfirm(
    name: string,
    description: string,
    imgInfo: { key: string; frame?: number } | undefined,
    onDrink: () => void,
  ): void {
    const c = this.add.container(0, 0).setDepth(60);

    const backdrop = this.add
      .rectangle(187, 333, 375, 667, 0x000000, 0.82)
      .setOrigin(0.5)
      .setInteractive();
    backdrop.on('pointerdown', () => c.destroy());
    c.add(backdrop);

    // Name
    c.add(addCrispText(this, 187, 80, name, { color: '#d4af37', fontSize: '18px' }).setOrigin(0.5));

    // Image
    if (imgInfo && this.textures.exists(imgInfo.key)) {
      c.add(
        this.add
          .image(187, 230, imgInfo.key, imgInfo.frame)
          .setDisplaySize(200, 200)
          .setOrigin(0.5),
      );
    }

    // Description
    c.add(
      addCrispText(this, 187, 345, description, {
        color: '#cccccc',
        fontSize: '11px',
        wordWrap: { width: 300 },
        align: 'center',
      }).setOrigin(0.5, 0),
    );

    // Cancel button
    const cancelBtn = this.add
      .rectangle(110, 470, 140, 40, 0x333333)
      .setInteractive({ useHandCursor: true });
    c.add(cancelBtn);
    c.add(
      addCrispText(this, 110, 470, 'Cancel', { color: '#aaaaaa', fontSize: '13px' }).setOrigin(0.5),
    );
    cancelBtn.on('pointerdown', () => c.destroy());

    // Drink button
    const drinkBtn = this.add
      .rectangle(265, 470, 140, 40, 0x064020)
      .setStrokeStyle(1, 0xd4af37)
      .setInteractive({ useHandCursor: true });
    c.add(drinkBtn);
    c.add(
      addCrispText(this, 265, 470, 'Drink', { color: '#d4af37', fontSize: '13px' }).setOrigin(0.5),
    );
    drinkBtn.on('pointerdown', () => {
      c.destroy();
      onDrink();
    });
  }

  private _doUseConsumable(id: string): void {
    const consumable = this.gs.getState().consumables.find((c) => c.id === id);
    if (!consumable) return;
    const def = CONSUMABLES_BY_ID[consumable.defId];
    if (!def) return;

    const eff = def.effect;
    const desc = def.description;
    const imgInfo = consumableSpritesheet(consumable.defId);

    // shuffleColumns / shuffleRows: board-restructuring — clear placements first, then apply
    if (eff.type === 'shuffleColumns' || eff.type === 'shuffleRows') {
      if (this.placements.length > 0) this._clearAllPlacements();
      useConsumable(this.gs, id);
      this.refreshConsumableTray();
      this.refreshTable();
      this.refreshWheelScoreSlices();
      return;
    }

    // Money-class consumables: fire immediately (no target needed)
    if (eff.type === 'doubleOrNothing' || eff.type === 'looseChange' || eff.type === 'compound') {
      useConsumable(this.gs, id);
      this.refreshConsumableTray();
      this.refreshHud();
      return;
    }

    // loadedBall: pick a number 0..36
    if (eff.type === 'loadedBall') {
      this.showNumberPicker(
        (n) => {
          const override: ConsumableEffect = { type: 'loadedBall', number: n };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshHud();
        },
        desc,
        imgInfo,
      );
      return;
    }

    // skipSlot: pick a number to remove from deck
    if (eff.type === 'skipSlot') {
      this.showNumberPicker(
        (n) => {
          const override: ConsumableEffect = { type: 'skipSlot', number: n };
          useConsumable(this.gs, id, override);
          this.wheel.setSlots(this.gs.getState().wheel);
          this.refreshConsumableTray();
          this.refreshHud();
          this.refreshWheelScoreSlices();
        },
        desc,
        imgInfo,
      );
      return;
    }

    // mint: pick a number to add to deck
    if (eff.type === 'mint') {
      this.showNumberPicker(
        (n) => {
          const override: ConsumableEffect = { type: 'mint', number: n };
          useConsumable(this.gs, id, override);
          this.wheel.setSlots(this.gs.getState().wheel);
          this.refreshConsumableTray();
          this.refreshHud();
          this.refreshWheelScoreSlices();
        },
        desc,
        imgInfo,
      );
      return;
    }

    // flipColor / bleach: pick one cell index
    if (eff.type === 'flipColor' || eff.type === 'bleach') {
      const effType = eff.type;
      this.showCellPicker(
        (cellIdx) => {
          const override: ConsumableEffect =
            effType === 'flipColor' ? { type: 'flipColor', cellIdx } : { type: 'bleach', cellIdx };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshHud();
          this.refreshTable();
          this.refreshWheelScoreSlices();
        },
        undefined,
        desc,
        imgInfo,
      );
      return;
    }

    // swapCells / merge: board-restructuring — clear placements first, then apply
    if (eff.type === 'swapCells' || eff.type === 'merge') {
      const effType = eff.type;
      this.showTwoCellPicker(
        (cellIdxA, cellIdxB) => {
          if (this.placements.length > 0) this._clearAllPlacements();
          const override: ConsumableEffect =
            effType === 'swapCells'
              ? { type: 'swapCells', cellIdxA, cellIdxB }
              : { type: 'merge', cellIdxA, cellIdxB };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshHud();
          this.refreshTable();
        },
        desc,
        imgInfo,
      );
      return;
    }

    // boostChips: pick a straight-bet (number) cell
    if (eff.type === 'boostChips') {
      this.showCellPicker(
        (cellIdx) => {
          const override: ConsumableEffect = { type: 'boostChips', cellIdx };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshTable();
          this.refreshWheelScoreSlices();
        },
        ['straight'],
        desc,
        imgInfo,
      );
      return;
    }

    // boostSubcell: pick a subcell (outside-sub, dozen-sub)
    if (eff.type === 'boostSubcell') {
      this.showCellPicker(
        (cellIdx) => {
          const override: ConsumableEffect = { type: 'boostSubcell', cellIdx };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshTable();
          this.refreshWheelScoreSlices();
        },
        ['outside-sub', 'dozen-sub'],
        desc,
        imgInfo,
      );
      return;
    }

    // boostMult: pick any cell
    if (eff.type === 'boostMult') {
      this.showCellPicker(
        (cellIdx) => {
          const override: ConsumableEffect = { type: 'boostMult', cellIdx };
          useConsumable(this.gs, id, override);
          this.refreshConsumableTray();
          this.refreshTable();
          this.refreshWheelScoreSlices();
        },
        undefined,
        desc,
        imgInfo,
      );
      return;
    }
  }

  /**
   * Show a number picker (0..36) as a Phaser Container overlay.
   * Tapping a number button calls callback and dismisses the picker.
   */
  private showNumberPicker(
    callback: (n: SlotNumber) => void,
    description?: string,
    imageInfo?: { key: string; frame?: number },
  ): void {
    const container = this.add.container(0, 0).setDepth(50);

    // Semi-transparent backdrop
    const backdrop = this.add
      .rectangle(187, 333, 375, 667, 0x000000, 0.7)
      .setOrigin(0.5, 0.5)
      .setInteractive(); // block input behind
    container.add(backdrop);

    const title = addCrispText(this, 187, 30, 'Pick a number (0–36)', {
      color: '#d4af37',
      fontSize: '14px',
    }).setOrigin(0.5, 0.5);
    container.add(title);

    if (description) {
      const sub = addCrispText(this, 187, 48, description, {
        color: '#aaaaaa',
        fontSize: '10px',
        wordWrap: { width: 340 },
        align: 'center',
      }).setOrigin(0.5, 0);
      container.add(sub);
    }

    // 37 number buttons in a grid (7 columns)
    const cols = 7;
    const btnW = 40;
    const btnH = 28;
    const startX = 187 - ((cols - 1) / 2) * (btnW + 4);
    const startY = description ? 100 : 70;

    // Consumable image preview
    if (imageInfo && this.textures.exists(imageInfo.key)) {
      const img = this.add
        .image(187, startY + 6 * (btnH + 4) + 16, imageInfo.key, imageInfo.frame)
        .setDisplaySize(100, 100)
        .setOrigin(0.5, 0)
        .setAlpha(0.9);
      container.add(img);
    }

    const cancelY = startY + 6 * (btnH + 4) + (imageInfo ? 128 : 24);
    const cancelBtn = this.add
      .rectangle(187, cancelY, 140, 32, 0x333333)
      .setInteractive({ useHandCursor: true });
    const cancelLabel = addCrispText(this, 187, cancelY, 'Cancel', {
      color: '#ffffff',
      fontSize: '12px',
    }).setOrigin(0.5, 0.5);
    container.add(cancelBtn);
    container.add(cancelLabel);
    cancelBtn.on('pointerdown', () => {
      container.destroy();
    });

    for (let num = 0; num <= 36; num++) {
      const col = num % cols;
      const row = Math.floor(num / cols);
      const bx = startX + col * (btnW + 4);
      const by = startY + row * (btnH + 4);

      const btn = this.add
        .rectangle(bx, by, btnW, btnH, 0x1a4a1a)
        .setInteractive({ useHandCursor: true });
      const lbl = addCrispText(this, bx, by, String(num), {
        color: '#ffffff',
        fontSize: '11px',
      }).setOrigin(0.5, 0.5);
      container.add(btn);
      container.add(lbl);

      const capturedNum = num as SlotNumber;
      btn.on('pointerdown', () => {
        container.destroy();
        callback(capturedNum);
      });
    }
  }

  /**
   * Show a board-spatial cell picker. callback receives the flat cells[] index.
   * Used by single-cell consumables: flip_color, bleach, boostChips, boostSubcell, boostMult.
   * allowedKinds: if provided, only cells with bet.kind in the list are interactive.
   */
  private showCellPicker(
    callback: (cellIdx: number) => void,
    allowedKinds?: string[],
    description?: string,
    imageInfo?: { key: string; frame?: number },
  ): void {
    if (this._activePicker?.isOpen()) return; // prevent double-tap
    const picker = new CellPickerModal(this, this.gs.getState().boardLayout);
    this._activePicker = picker;
    picker.open(
      'Select a cell',
      (cellIdx) => {
        this._activePicker = null;
        callback(cellIdx);
      },
      undefined,
      allowedKinds,
      description,
      imageInfo,
    );
  }

  /**
   * Two-step cell picker. CR-02: second step excludes first index.
   * Pitfall 4: cancel on EITHER step destroys both pickers (no partial-mutation possible).
   * Used by two-cell consumables: swap_cells, merge.
   */
  private showTwoCellPicker(
    callback: (a: number, b: number) => void,
    description?: string,
    imageInfo?: { key: string; frame?: number },
  ): void {
    if (this._activePicker?.isOpen()) return; // prevent double-tap
    const layout = this.gs.getState().boardLayout;
    const firstPicker = new CellPickerModal(this, layout);
    this._activePicker = firstPicker;
    firstPicker.open(
      'Select 1st cell',
      (firstIdx) => {
        const secondPicker = new CellPickerModal(this, layout);
        this._activePicker = secondPicker;
        secondPicker.open(
          `Select 2nd cell (first: ${firstIdx})`,
          (secondIdx) => {
            this._activePicker = null;
            callback(firstIdx, secondIdx);
          },
          firstIdx,
          undefined,
          description,
          imageInfo,
        );
      },
      undefined,
      undefined,
      description,
      imageInfo,
    );
  }

  /**
   * Re-create the Table from current boardLayout.
   * Called after any board mutation (flipColor, bleach, swapCells, merge) so
   * the visual board reflects the mutation before the next spin.
   */
  private refreshTable(): void {
    this.table.destroy();
    this.table = new Table(this, 0, 450, this.gs.getState().boardLayout, {
      boardWidth: 375,
      boardHeight: 130,
    });
    // Rebuild reachable-set previews in case any chess placements were active
    this.rebuildAllChessPreviews();
    // Boss cell-blocker overlay must redraw against the (possibly fresh) Table.
    this.refreshBossOverlay();
  }

  /** Recall all placed fiches (reset strokes, destroy chips, clear arrays). */
  private _clearAllPlacements(): void {
    this.placementRectSets.forEach((rects) =>
      rects.forEach((r) => r.setStrokeStyle(0.5, 0xffffff)),
    );
    this.placementChips.forEach((chip) => chip.destroy());
    this.placementRectSets = [];
    this.placementChips = [];
    this.placements = [];
    this.passedFicheIds.clear();
    this.previewGraphics.clear();
    this.tray.markUsed([]);
    this.refreshSpinButton();
    this.refreshBlockedCellsOverlay();
    this.refreshWheelScoreSlices();
  }

  private refreshSpinButton(): void {
    const committed = this.placements.length + this.passedFicheIds.size;
    const ready = committed >= this.requiredFiches;
    // Phase 13 (UI-11): spinButton is now an Arc — setFillStyle works the same as Rectangle.
    this.spinButton.setFillStyle(ready ? 0xd4af37 : 0x333333);
    this.spinButtonLabel.setColor(ready ? '#000000' : '#888888');
  }

  private async onSpinTapped(): Promise<void> {
    if (this.isSpinning) return;
    const committed = this.placements.length + this.passedFicheIds.size;
    if (committed < this.requiredFiches) return;
    this.isSpinning = true;
    // Slices stay visible during the spin so the player can see which slot wins.
    // Phase 14: clear chess reachable-set preview on spin start (UI-SPEC §Interaction).
    this.previewGraphics.clear();

    const s = this.gs.getState();
    // 12b (BALL-09): cycle priority — queuedBall first if the second_ball
    // relic filled it, otherwise nextBall (v1 behavior). Capture the
    // queuedBall presence BEFORE the spin pipeline runs so post-spin we can
    // consumeQueuedBall vs cycleBall without TOCTOU on intermediate state.
    const usedQueuedBall = s.queuedBall !== null;
    const ball = s.queuedBall ?? s.nextBall;

    // Run onSpinStart relic hooks (lucky_charm, house_edge, cold_streak, hot_streak, void_walker).
    const spinCtx = runSpinStart(s.relics, {
      ball,
      isFirstSpinOfRound: this.currentSpin === 0,
      consecutiveRedHits: this.streakRed,
      consecutiveBlackHits: this.streakBlack,
      flatMultBonus: 0,
      payoutMultiplier: 1,
      spinMoneyDelta: 0,
      // Phase 13 (RELIC-09): consumed by void_walker.onSpinStart.
      voidPositionsCount: s.voidPositions.length,
    });
    // Phase 23 (FICHE-23): tetris-row mult is a permanent flatMultBonus; one
    // bonus per row already cleared by tetris fiches in a single prior spin.
    spinCtx.flatMultBonus += s.tetrisCompletedRows.length;

    // Apply spinMoneyDelta (e.g. House Edge -$1 per spin, regardless of hit/miss).
    if (spinCtx.spinMoneyDelta !== 0) {
      this.gs.adjustMoney(spinCtx.spinMoneyDelta);
    }

    // Phase 13 (WHEEL-08): pass the run's composed wheel to pickSlotForSpin so
    // void-aware bias works end-to-end (BOARD-06 + 13-UI-SPEC §Wheel).
    // Phase 15 (CONSUMABLE-06): pass forcedNumber so loaded_ball consumable resolves correctly.
    // CR-03 fix: capture forcedNumber from state then clear it BEFORE the safety_net
    // reroll block. The reroll is always free (forcedNumber: null) so a loaded_ball
    // that forces 0 cannot trap the player in an infinite-zero loop with safety_net.
    const forcedNumber = s.forcedNumber;
    this.gs.setForcedNumber(null); // consumed by the initial spin; must not leak into reroll
    let slot = pickSlotForSpin({
      ball,
      rng: this.gs.getRng(),
      placements: this.placements,
      wheel: s.wheel, // ← NEW (13-05): pass run's composed wheel
      forcedNumber, // ← Phase 15 (CONSUMABLE-06): loaded_ball consumable
    });

    // Phase 13 (BETS-09): safety_net only triggers on number-zero, NOT void.
    // Void slots are full losses (isLosingSlot returns true) — no refund.
    // Reroll fires AT MOST once per round; the re-rolled slot may itself be 0 (rare, accepted).
    if (slot.kind === 'number' && slot.value === 0) {
      const zeroCtx = runOnZero(s.relics, {
        isFirstZeroOfRound: this.zerosThisRound === 0,
        shouldReroll: false,
      });
      this.zerosThisRound++;
      if (zeroCtx.shouldReroll) {
        // CR-03 fix: reroll is always free — forcedNumber already consumed and
        // cleared above. Passing it here would make safety_net useless when the
        // player forced 0 with a loaded_ball consumable.
        slot = pickSlotForSpin({
          ball,
          rng: this.gs.getRng(),
          placements: this.placements,
          wheel: s.wheel, // ← also pass wheel on reroll
          forcedNumber: null, // ← reroll is free; forcedNumber was consumed by initial spin
        });
      }
    }

    // Animate the wheel to that slot's position in the composed wheel array.
    // Phase 13: use slot.position directly (aligns 1:1 with the composed Slot[])
    // instead of WHEEL_ORDER.indexOf(slot.number) which only works for the static wheel.
    if (ball) this.wheel.setBallDefId(ball.defId);
    const targetIndex = slot.position;
    await this.wheel.spinTo(targetIndex);

    // zeroFlood boss: 10 random wheel slots act as 0 every spin. Determined fresh per spin.
    const effectiveMods = getEffectiveMods(this.bossModifier);
    if (effectiveMods.some((m) => m.type === 'zeroFlood')) {
      const floodedIndices = computeFloodedIndices(this.gs.getRng(), s.wheel.length);
      if (floodedIndices.has(targetIndex)) {
        slot = {
          kind: 'number',
          value: 0,
          color: 'green',
          position: targetIndex,
          modifier: null,
        };
      }
    }

    // Show where the ball landed: gold ring + result label.
    this.wheel.showResult(slot);

    // Resolve scores with relic-driven adjustments
    const fichesById: Record<string, Fiche> = {};
    s.ficheHand.forEach((f) => {
      fichesById[f.id] = f;
    });

    // Phase 14 (SCORING-06 / RELIC-10): build expandedBets pre-pass for chess fiches.
    // expandPlacement() returns [placement.bet] for non-chess fiches (identity passthrough),
    // so this map is safe to pass for all placements without changing existing score behavior.
    //
    // Invariant (FI-05): ficheId values MUST be unique across placements because the Map key
    // is ficheId — a duplicate would silently overwrite the earlier placement's expansion,
    // dropping that fiche's chess coverage from scoring. The UI enforces this (each fiche can
    // only be placed once per spin), but we assert defensively here to catch future regressions.
    const expandedBets = new Map<string, BetType[]>();
    const spinBossBlockers = this._computeBossBlockerSet(s.boardLayout);
    for (const placement of this.placements) {
      if (expandedBets.has(placement.ficheId)) {
        console.warn(
          '[RoundScene] duplicate ficheId in placements — scoring will be incorrect:',
          placement.ficheId,
        );
      }
      const fiche = fichesById[placement.ficheId];
      if (fiche) {
        const expansion = expandPlacement(
          placement,
          s.boardLayout,
          fiche,
          this.placements,
          spinBossBlockers,
        );
        expandedBets.set(placement.ficheId, expansion);
      }
    }

    // Phase 16 (FICHE-15): count 'standard' fiches across the entire deck
    // (draw + discard + hand) for standardFicheCountBonus effect resolution.
    const standardFicheCount = [...s.ficheDeck, ...s.ficheDiscard, ...s.ficheHand].filter(
      (f) => f.defId === 'standard',
    ).length;

    // Splash ball: compute adjacent wheel slots to score at reduced payout.
    const extraPayoutSlots: { slot: Slot; scale: number }[] = [];
    if (ball?.effect?.type === 'splashPayout') {
      const sideRatio = ball.effect.sideRatio;
      const wheelArr = s.wheel;
      const pos = slot.position;
      const leftSlot = wheelArr[(pos - 1 + wheelArr.length) % wheelArr.length];
      const rightSlot = wheelArr[(pos + 1) % wheelArr.length];
      if (leftSlot) extraPayoutSlots.push({ slot: leftSlot, scale: sideRatio });
      if (rightSlot) extraPayoutSlots.push({ slot: rightSlot, scale: sideRatio });
    }

    const bossRedHalf = effectiveMods.some((m) => m.type === 'redHalf');
    const bossBlackHalf = effectiveMods.some((m) => m.type === 'blackHalf');

    // nudge boss: compute scores for the landing slot + its 2 immediate neighbors,
    // then move the ball to whichever scores LOWEST for the player.
    if (effectiveMods.some((m) => m.type === 'nudge')) {
      const wheel = s.wheel;
      const candidatePositions = [
        (targetIndex - 1 + wheel.length) % wheel.length,
        targetIndex,
        (targetIndex + 1) % wheel.length,
      ];
      const candidates = candidatePositions
        .map((pos) => wheel[pos])
        .filter((cs): cs is Slot => Boolean(cs));
      if (candidates.length === 3) {
        const nudgeScores = candidates.map(
          (candidateSlot) =>
            scoreSpin({
              placements: this.placements,
              fichesById,
              slot: candidateSlot,
              ball,
              payoutMultiplier: spinCtx.payoutMultiplier,
              flatMultBonus: spinCtx.flatMultBonus,
              bossHalfPayouts: false,
              bossRedHalf,
              bossBlackHalf,
              expandedBets,
              boardLayout: s.boardLayout,
              standardFicheCount,
              lastSpin: s.lastSpin,
              oneForAll: s.relics.some((r) => r.defId === 'one_for_all'),
            }).spinScore,
        );
        const minScore = Math.min(...nudgeScores);
        const nudgedIdx = nudgeScores.indexOf(minScore);
        if (nudgedIdx !== 1) {
          slot = candidates[nudgedIdx]!;
          this.wheel.showResult(slot);
        }
      }
    }

    const result = scoreSpin({
      placements: this.placements,
      fichesById,
      slot,
      ball,
      payoutMultiplier: spinCtx.payoutMultiplier,
      flatMultBonus: spinCtx.flatMultBonus,
      bossHalfPayouts: false,
      bossRedHalf,
      bossBlackHalf,
      expandedBets,
      boardLayout: s.boardLayout,
      standardFicheCount,
      lastSpin: s.lastSpin,
      oneForAll: s.relics.some((r) => r.defId === 'one_for_all'),
      ...(extraPayoutSlots.length > 0 ? { extraPayoutSlots } : {}),
    });
    this.gs.recordSpin(result);
    this.gs.updateRoundFicheScores(result.perFicheScores);

    // Phase 23 (FICHE-23, pawn promotion): walk every pawn placement this spin
    // and update its column/row streak. Collect ficheIds that earned promotion
    // (streak=4, row=0) without showing the modal yet — the caller decides
    // whether to chain the modal before a round-clear or show it immediately.
    const pendingPromotions: string[] = [];
    {
      const sNow = this.gs.getState();
      for (const p of this.placements) {
        const fiche = sNow.ficheHand.find((f) => f.id === p.ficheId);
        if (fiche?.effect?.type !== 'chessMove' || fiche.effect.piece !== 'pawn') continue;
        const cell = sNow.boardLayout.cells.find((c) => this.betsMatchForPreview(c.bet, p.bet));
        if (!cell) continue;
        this.gs.updatePawnProgress(fiche.id, cell.col, cell.row);
        const prog = this.gs.getState().pawnProgress[fiche.id]!;
        if (prog.streak >= 4 && cell.row === 0) {
          pendingPromotions.push(fiche.id);
        }
      }
    }

    // Phase 23 (FICHE-23, tetris row mult): if every straight-bet cell on a
    // numeric row (rows 1-3, cols 1-12) is covered by a tetris fiche this
    // spin, that row permanently grants +1 flatMultBonus for the rest of
    // the run. Rows already completed earlier are skipped.
    {
      const st = this.gs.getState();
      const tetrisCovered = new Set<string>();
      for (const p of this.placements) {
        const fiche = st.ficheHand.find((f) => f.id === p.ficheId);
        if (!fiche || (fiche.effect?.type as string) !== 'tetrisShape') continue;
        const expansion = expandPlacement(p, st.boardLayout, fiche);
        for (const bet of expansion) {
          if (bet.kind === 'straight') tetrisCovered.add(`${bet.number}`);
        }
      }
      for (const row of [1, 2, 3] as const) {
        const rowNums = st.boardLayout.cells
          .filter((c) => c.row === row && c.col >= 1 && c.col <= 12 && c.bet.kind === 'straight')
          .map((c) => `${(c.bet as { number: number }).number}`);
        if (rowNums.length === 12 && rowNums.every((n) => tetrisCovered.has(n))) {
          const isNew = this.gs.completeTetrisRow(row);
          if (isNew) this.showRowCompleteToast(row);
        }
      }
    }

    // Phase 16 (ROUND-07, FICHE-13): populate lastSpin and update currentStreak.
    // Use a block scope to avoid naming collisions with existing local variables.
    {
      const s16 = this.gs.getState();
      const hitCount16 = result.perFicheScores.filter((ps) => ps.hit).length;
      const wasZero16 = result.slot.kind === 'number' && result.slot.value === 0;
      const newStreak = hitCount16 > 0 ? s16.currentStreak + 1 : 0;
      const spin16: SpinResult16 = {
        slot: result.slot,
        totalScore: result.spinScore,
        hitCount: hitCount16,
        wasZero: wasZero16,
        streak: newStreak,
      };
      this.gs.setCurrentStreak(newStreak);
      this.gs.setLastSpin(spin16);
    }

    // Phase 19 (FICHE-19): Cell Swapper post-score dispatch.
    // For each placed cell_swapper fiche, swap its origin cell with a
    // random orthogonal neighbor. Pure side-effect: no chips/mult change
    // (handled by no-op case in scoring.applyFicheEffect). Reuses the
    // swapCells reducer pattern from consumables (Phase 15).
    {
      const ficheHandSnap = this.gs.getState().ficheHand;
      // Snapshot the layout ONCE before the loop so that two simultaneous
      // cell_swapper placements each see the pre-mutation board when resolving
      // their origin/target. Without this snapshot the second fiche would see
      // the already-mutated layout from the first swap, producing an unspecified
      // cascaded swap instead of two independent swaps (WR-03 fix, Phase 19).
      const layoutSnap = structuredClone(this.gs.getState().boardLayout);
      for (const placement of this.placements) {
        const fiche = ficheHandSnap.find((f) => f.id === placement.ficheId);
        if (!fiche) continue;
        const def = FICHES_BY_ID[fiche.defId];
        if (def?.effect?.type !== 'cellSwap') continue;

        // Find the origin cell by structural bet match (mirrors the
        // betsMatchForPreview helper already in this scene).
        // Use layoutSnap so concurrent cell_swappers resolve independently.
        const origin = layoutSnap.cells.find((c) => this.betsMatchForPreview(c.bet, placement.bet));
        if (!origin) continue; // void / unmatched origin → silent skip

        const neighbors = orthogonalNeighbors(layoutSnap, origin);
        if (neighbors.length === 0) continue; // defensive: no neighbors → skip

        // Determinism: 1 rng() call per cell_swapper placement.
        const target = pickFromArray(this.gs.getRng(), neighbors);
        const originIdx = layoutSnap.cells.indexOf(origin);
        // Use findIndex by (row,col) instead of indexOf(target) — JSON save/load
        // breaks the shared object refs between cells[] and grid[][], making
        // indexOf return -1 and silently skipping the swap after page reload.
        const targetIdx = layoutSnap.cells.findIndex(
          (c) => c.row === target.row && c.col === target.col,
        );
        if (originIdx < 0 || targetIdx < 0) continue;

        // Same swap pattern as 'swapCells' consumable (Phase 15):
        // swap the bet/payoutMult/colorOverride/bleached fields, keep each
        // cell's row/col anchor unchanged.
        const swappedOrigin: BoardCell = {
          ...target,
          row: origin.row,
          col: origin.col,
        };
        const swappedTarget: BoardCell = {
          ...origin,
          row: target.row,
          col: target.col,
        };
        this.gs.mutateBoardCell(originIdx, swappedOrigin);
        this.gs.mutateBoardCell(targetIdx, swappedTarget);
        logEvent('cell_swap', {
          from: { row: origin.row, col: origin.col, bet: origin.bet },
          to: { row: target.row, col: target.col, bet: target.bet },
        });
      }
      // Rebuild the Table UI so the visual board matches the mutated layout.
      // Matches the refreshTable() pattern at every other board-mutation site
      // (consumable swapCells, flipColor, bleach, merge — see ~lines 589–604).
      this.refreshTable();
    }

    // CR-03: forcedNumber is already cleared above (before the reroll block) so
    // it cannot accidentally propagate into a safety_net reroll. The earlier
    // setForcedNumber(null) also satisfies T-15-17.
    this.roundScore += result.spinScore;
    logEvent('spin', {
      spin: this.currentSpin + 1,
      slot: result.slot,
      placements: result.placements.map((p) => ({
        bet: p.bet,
        fiche: this.gs.getState().ficheHand.find((f) => f.id === p.ficheId)?.defId ?? p.ficheId,
      })),
      perFiche: result.perFicheScores.map((ps) => ({
        fiche: this.gs.getState().ficheHand.find((f) => f.id === ps.ficheId)?.defId ?? ps.ficheId,
        hit: ps.hit,
        chips: ps.chips,
        mult: ps.mult,
        payout: ps.payout,
        total: ps.total,
      })),
      spinScore: result.spinScore,
      roundScore: this.roundScore,
    });

    // Phase 13: update streaks — narrow on slot.kind before reading slot.color.
    // Void slots reset streaks (treated as neutral, similar to green/0).
    if (slot.kind === 'number') {
      if (slot.color === 'red') {
        this.streakRed++;
        this.streakBlack = 0;
      } else if (slot.color === 'black') {
        this.streakBlack++;
        this.streakRed = 0;
      } else {
        // green / 0: streaks reset
        this.streakRed = 0;
        this.streakBlack = 0;
      }
    } else {
      // void slot: streaks reset (full loss; same neutral treatment as green/0)
      this.streakRed = 0;
      this.streakBlack = 0;
    }

    // Phase 17 (UI-10): staggered score pops via ScorePopManager (100ms per fiche, 4-cap).
    const ficheCount = result.perFicheScores.length;
    result.perFicheScores.forEach((ps, idx) => {
      const popX = this.tray.slotCenterX(idx, ficheCount);
      const popY = 471; // 25px above fiche tray (y=496)
      this.scorePopManager.pop(popX, popY, ps.total, ps.hit, idx * 100);
    });
    // Show score breakdown ~350ms after last pop (pops are 100ms apart, up to 4 fiches).
    const breakdownDelay = ficheCount * 100 + 350;
    this.time.delayedCall(breakdownDelay, () => this.showScoreBreakdown(result));

    // Reset placement-cell strokes and remove chip tokens
    this.placementRectSets.forEach((rects) =>
      rects.forEach((r) => r.setStrokeStyle(0.5, 0xffffff)),
    );
    this.placementChips.forEach((chip) => chip.destroy());
    this.placementRectSets = [];
    this.placementChips = [];
    this.placements = [];
    // Phase 14: clear chess reachable-set preview on spin completion (UI-SPEC §Interaction).
    this.previewGraphics.clear();
    this.refreshBlockedCellsOverlay();
    // Boss overlay persists across spins (only clears when the round ends).
    this.bossOverlayGraphics.clear();
    this.refreshBossOverlay();
    // Restore tray opacity (no fiches "in use" anymore — they're discarded).
    this.passedFicheIds.clear();
    this.tray.markUsed([]);

    // HUD reflects new roundScore
    this.refreshHud();

    // currentSpin counts spins TAKEN this round; increment before round-end checks.
    this.currentSpin++;

    // Round-clear / round-fail checks BEFORE drawing the next hand.
    const target = this.getRoundTarget();
    if (this.roundScore >= target) {
      // Show any pawn promotion modals before leaving the round — the player
      // must pick their new piece before the scene transitions to the shop.
      this._chainPromotionsThen(pendingPromotions, () => {
        this.handleRoundClear();
        this.isSpinning = false;
      });
      return;
    }
    if (this.currentSpin >= 3) {
      this.handleRoundFail();
      this.isSpinning = false;
      return;
    }
    // Non-terminal spin: show promotion modals immediately (game continues
    // to the next spin while the player interacts with the modal).
    for (const ficheId of pendingPromotions) {
      this.showPawnPromotionModal(ficheId);
    }

    // Otherwise: discard the placed fiches, draw the relic-driven required count.
    // Phase 16 (RELIC-08 CR-01 fix): peek call is placed HERE — immediately before
    // discardHand()+drawFiches() — so no RNG is consumed between peekDrawFiches()
    // and drawFiches(). pickSlotForSpin() runs before this block, so moving peek
    // here eliminates the RNG-position divergence that caused the in-game preview
    // to predict wrong fiches. "Peek shows X → round delivers X" is now guaranteed.
    {
      const hasPeek = this.gs.getState().relics.some((r) => r.defId === 'peek');
      if (hasPeek) {
        const preview = this.gs.peekDrawFiches(this.requiredFiches);
        const firstPreview = preview[0];
        if (firstPreview) {
          this.showPeekOverlay(firstPreview);
        }
      }
    }
    this.gs.discardHand();
    this.gs.drawFiches(this.requiredFiches);
    this.gs.applyHandRelics(this.requiredFiches);
    // 12b: if the queuedBall was at the front of the priority chain this
    // spin, just clear that slot — nextBall stays unchanged so it becomes
    // the primary on the next spin. Otherwise, v1 behavior: cycle nextBall.
    if (usedQueuedBall) {
      this.gs.consumeQueuedBall();
    } else {
      this.gs.cycleBall();
    }

    // Re-render tray with new hand + rebind tap handlers
    this.refresh();
    this.refreshSpinButton();
    this.refreshConsumableTray();

    this.isSpinning = false;
  }

  // -------- round-end handlers --------

  private getRoundTarget(): number {
    const s = this.gs.getState();
    const ante = ANTES.find((a) => a.ante === s.ante)!;
    const kinds = ['small', 'big', 'boss'] as const;
    return ante.targets[kinds[s.roundIndex]];
  }

  private getRoundReward(): number {
    const s = this.gs.getState();
    const ante = ANTES.find((a) => a.ante === s.ante)!;
    const kinds = ['small', 'big', 'boss'] as const;
    return ante.rewards[kinds[s.roundIndex]];
  }

  private handleRoundClear(): void {
    const s = this.gs.getState();
    logEvent('round_clear', { ante: s.ante, round: s.roundIndex, score: this.roundScore });
    const baseReward = this.getRoundReward();
    const unusedSpins = Math.max(0, 3 - this.currentSpin);
    const baseInterestCap = s.relics.some((r) => r.defId === 'compound_interest') ? 10 : 5;
    // Phase 15 (CONSUMABLE-04, T-15-18): compound consumable raises cap by pendingInterestBonus.
    // Applied once at round boundary; cleared immediately after to prevent double-apply.
    const interestCap = baseInterestCap + s.pendingInterestBonus;
    const interest = Math.min(interestCap, Math.floor(s.money / 5));
    const total = baseReward + unusedSpins + interest;
    this.gs.adjustMoney(total);
    // T-15-18 mitigation: clear pendingInterestBonus AFTER applying (not before; not on fail).
    this.gs.setPendingInterestBonus(0);
    saveRun(this.gs.toJSON());

    // Phase 19 (CONSUMABLE-19): non-boss rounds show a "pick 1 of 2"
    // consumable reward overlay before the shop opens. Boss rounds
    // (roundIndex === 2) skip the modal entirely.
    const isBossRound = this.gs.getState().roundIndex === 2;
    if (isBossRound) {
      this.scene.start('Shop', { gameState: this.gs });
      return;
    }
    this.showConsumableRewardModal(() => {
      saveRun(this.gs.toJSON()); // re-save in case a consumable was added
      this.scene.start('Shop', { gameState: this.gs });
    });
  }

  /**
   * Phase 19 (CONSUMABLE-19): Reward overlay shown on non-boss round clear.
   * Samples 2 ConsumableDefs from CONSUMABLES (without replacement, deterministic
   * via this.gs.getRng()), shows them as side-by-side tappable cards plus a
   * SKIP button. On pick, addConsumable is called (silent no-op at cap 3).
   * On dismiss (pick or skip), invokes onDismiss() — the caller advances to
   * the shop scene there.
   *
   * Depth stacking mirrors ConfirmModal: 200=backdrop, 201=cards, 202=button,
   * 203=text. The `resolved` flag prevents double-tap from triggering both a
   * card and skip.
   *
   * Determinism: consumes exactly 2 rng() calls (pickFromArray × 2).
   */
  private showConsumableRewardModal(onDismiss: () => void): void {
    // Sample 2 distinct ConsumableDefs (without replacement).
    const pool: ConsumableDef[] = [...CONSUMABLES];
    const options: ConsumableDef[] = [];
    for (let i = 0; i < 2 && pool.length > 0; i++) {
      const picked = pickFromArray(this.gs.getRng(), pool);
      pool.splice(pool.indexOf(picked), 1);
      options.push(picked);
    }
    if (options.length < 2) {
      // Defensive fallback: pool too small (won't happen with 10 defs).
      // Skip the modal entirely.
      onDismiss();
      return;
    }

    let resolved = false;
    const created: Phaser.GameObjects.GameObject[] = [];

    const backdrop = this.add
      .rectangle(0, 0, 375, 667, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setDepth(200)
      .setInteractive(); // swallow taps that miss buttons
    created.push(backdrop);

    const title = addCrispText(this, 187, 100, 'Round Reward', {
      color: '#ffffff',
      fontSize: '18px',
      fontStyle: 'bold',
    })
      .setOrigin(0.5)
      .setDepth(203);
    created.push(title);
    const subtitle = addCrispText(this, 187, 130, 'Choose 1 of 2 consumables', {
      color: '#aaaaaa',
      fontSize: '12px',
    })
      .setOrigin(0.5)
      .setDepth(203);
    created.push(subtitle);

    const rarityColor = (r: string): string => {
      switch (r) {
        case 'common':
          return '#ffffff';
        case 'uncommon':
          return '#88aaff';
        case 'rare':
          return '#cc44ff';
        case 'legendary':
          return '#d4af37';
        default:
          return '#ffffff';
      }
    };

    const cardCenters = [97, 277];
    const cardY = 320;
    const cardW = 150;
    const cardH = 190;

    const cleanup = (): void => {
      for (const obj of created) obj.destroy();
    };

    const onCardPick = (defId: string): void => {
      if (resolved) return;
      resolved = true;
      // Silent no-op at cap 3 (addConsumable handles the guard).
      this.gs.addConsumable(consumableFromDef(defId));
      cleanup();
      onDismiss();
    };

    const onSkip = (): void => {
      if (resolved) return;
      resolved = true;
      this.gs.adjustMoney(3);
      cleanup();
      onDismiss();
    };

    options.forEach((def, idx) => {
      const cx = cardCenters[idx]!;
      const card = this.add
        .rectangle(cx, cardY, cardW, cardH, 0x0a3d20)
        .setStrokeStyle(1, 0x444444)
        .setDepth(201)
        .setInteractive({ useHandCursor: true });
      card.on('pointerdown', () => onCardPick(def.defId));
      created.push(card);

      const name = addCrispText(this, cx, 240, def.name, {
        color: '#ffffff',
        fontSize: '14px',
        fontStyle: 'bold',
      })
        .setOrigin(0.5)
        .setDepth(203);
      created.push(name);

      const rarity = addCrispText(this, cx, 258, def.rarity, {
        color: rarityColor(def.rarity),
        fontSize: '11px',
      })
        .setOrigin(0.5)
        .setDepth(203);
      created.push(rarity);

      const desc = addCrispText(this, cx, 280, def.description, {
        color: '#cccccc',
        fontSize: '11px',
        wordWrap: { width: 130 },
        align: 'center',
      })
        .setOrigin(0.5, 0)
        .setDepth(203);
      created.push(desc);
    });

    const skipBtn = this.add
      .rectangle(187, 520, 120, 40, 0x333333)
      .setStrokeStyle(1, 0x555555)
      .setDepth(202)
      .setInteractive({ useHandCursor: true });
    skipBtn.on('pointerdown', onSkip);
    created.push(skipBtn);

    const skipLabel = addCrispText(this, 187, 520, 'SKIP (+$3)', {
      color: '#ffffff',
      fontSize: '14px',
    })
      .setOrigin(0.5)
      .setDepth(203);
    created.push(skipLabel);
  }

  private handleRoundFail(): void {
    const s = this.gs.getState();
    logEvent('round_fail', {
      ante: s.ante,
      round: s.roundIndex,
      score: this.roundScore,
      target: this.getRoundTarget(),
    });
    clearSave();
    this.scene.start('GameOver', {
      seed: s.seed,
      ante: s.ante,
      roundIndex: s.roundIndex,
    });
  }

  update(): void {
    this.wheel.tickSlices();
  }

  // -------- render --------

  private refresh(): void {
    const s = this.gs.getState();
    const cfg = ANTES[s.ante - 1]!;
    const target =
      s.roundIndex === 0
        ? cfg.targets.small
        : s.roundIndex === 1
          ? cfg.targets.big
          : cfg.targets.boss;
    this.hud.setHudState({
      ante: s.ante,
      roundLabel: ROUND_LABELS[s.roundIndex],
      money: s.money,
      score: this.roundScore,
      target,
      spin: this.currentSpin,
      maxSpins: 3,
    });
    if (s.nextBall) this.wheel.setBallDefId(s.nextBall.defId);
    // Phase 13: re-sync the wheel's slot composition on each refresh in case
    // a future consumable (Phase 15) mutates the wheel between rounds.
    // Cost is one redraw of 37 wedges — negligible.
    this.wheel.setSlots(s.wheel);
    this.relicBar.draw(
      s.relics.map((r) => ({
        defId: r.defId,
        name: r.name,
        description: r.description ?? '',
        rarity: r.rarity,
      })),
    );
    this.tray.draw(
      s.ficheHand.map((f) => ({
        id: f.id,
        defId: f.defId,
        chips: f.chips,
        rarity: f.rarity,
        name: f.name,
      })),
      this.requiredFiches,
    );
    // Phase 22: drag-to-place binding (replaces tap-select flow).
    this.tray.setOnFicheDragStart((ficheId, idx, ghost) =>
      this.onFicheDragStart(ficheId, idx, ghost),
    );
    this.tray.onPass = (ficheId) => {
      if (this.isSpinning) return;
      if (this.passedFicheIds.has(ficheId)) {
        this.passedFicheIds.delete(ficheId);
      } else {
        this.passedFicheIds.add(ficheId);
      }
      this.tray.markUsed(this.placements.map((p) => p.ficheId));
      this.tray.markPassed(Array.from(this.passedFicheIds));
      this.refreshSpinButton();
    };
  }

  private refreshHud(): void {
    const s = this.gs.getState();
    const cfg = ANTES[s.ante - 1]!;
    const target =
      s.roundIndex === 0
        ? cfg.targets.small
        : s.roundIndex === 1
          ? cfg.targets.big
          : cfg.targets.boss;
    this.hud.setHudState({
      ante: s.ante,
      roundLabel: ROUND_LABELS[s.roundIndex],
      money: s.money,
      score: this.roundScore,
      target,
      spin: this.currentSpin,
      maxSpins: 3,
    });
  }

  shutdown(): void {
    this.scorePopManager?.clear();
    this._activePicker?.destroy();
    this._activePicker = null;
  }
}
